import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, SlidersHorizontal, Star } from "lucide-react";
import { imgUri } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useCart } from "../lib/cart";
import { chf } from "../lib/format";

export function ProductCard({ product }) {
  const navigate = useNavigate();
  const { t, tx } = useI18n();
  const cart = useCart();
  const off = !product.available;
  const hasSizes = product.sizes.length > 1;
  const price = product.sizes[0]?.price ?? product.price;
  const open = () => navigate(`/product/${product.id}`);

  const quickAdd = (e) => {
    e.stopPropagation();
    if (off) return;
    if (product.customizable || hasSizes) return open();
    cart.addItem({
      product_id: product.id,
      name: product.name,
      image_url: product.image_url,
      unit_price: price,
      quantity: 1,
      size: product.sizes[0] ?? null,
      options: [],
      removed_ingredients: [],
      extras: [],
      note: "",
      is_alcohol: product.is_alcohol,
      alcohol_type: product.alcohol_type,
    });
    toast.success(`${tx(product.name)} – ${t("added")}`, { duration: 3000 });
  };

  return (
    <div className="mb-7 flex flex-col" data-testid={`product-card-${product.id}`}>
      <button
        type="button"
        onClick={open}
        className="group relative h-[210px] w-full overflow-hidden rounded-3xl bg-hm-tertiary shadow-lg shadow-black/10 transition-transform active:scale-[0.985]"
      >
        <img
          src={imgUri(product.image_url)}
          alt={tx(product.name)}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute left-3 top-3 flex gap-1.5">
          {product.highlight === "moment" ? (
            <span className="flex h-[26px] items-center gap-1.5 rounded-full bg-hm-brand px-2.5 font-sans text-[11px] font-black tracking-wide text-white" data-testid={`product-month-${product.id}`}>
              <Star size={11} /> {t("pizzaOfMonth")}
            </span>
          ) : null}
          {product.is_alcohol ? (
            <span className="flex h-[26px] items-center rounded-full bg-black/45 px-2.5 font-sans text-[11px] font-extrabold text-white">
              {product.alcohol_type === "spirits" ? "18+" : "16+"}
            </span>
          ) : null}
          {product.customizable ? (
            <span className="flex h-[26px] items-center gap-1.5 rounded-full bg-black/45 px-2.5 font-sans text-[11px] font-extrabold text-white">
              <SlidersHorizontal size={11} /> {t("customize")}
            </span>
          ) : null}
        </div>
        <div className="absolute bottom-3.5 left-4 right-4 flex items-end justify-between gap-2.5">
          <span className="line-clamp-1 flex-1 text-left font-display text-2xl text-white" data-testid={`product-name-${product.id}`}>
            {tx(product.name)}
          </span>
          <span className="flex h-8 items-center rounded-full bg-hm-surface px-3 font-sans text-sm font-extrabold text-hm-ink" data-testid={`product-price-${product.id}`}>
            {hasSizes ? `${t("from")} ` : ""}{chf(price)}
          </span>
        </div>
        {off ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/45">
            <span className="rounded-full border-[1.5px] border-white px-3.5 py-2 font-sans text-sm font-extrabold uppercase tracking-widest text-white">
              {t("soldOut")}
            </span>
          </div>
        ) : null}
      </button>

      <div className="px-1 pt-3">
        <p className="line-clamp-2 font-sans text-sm leading-5 text-hm-muted">{tx(product.description)}</p>
        <div className="mt-3 flex items-center gap-2.5">
          {product.customizable || hasSizes ? null : <div className="flex-1" />}
          <button
            type="button"
            onClick={quickAdd}
            disabled={off}
            data-testid={`product-add-${product.id}`}
            className="flex h-[46px] flex-1 items-center justify-center gap-1.5 rounded-full bg-hm-brand font-sans text-sm font-extrabold text-white transition-all hover:opacity-90 active:scale-[0.97] disabled:opacity-40"
          >
            <Plus size={16} /> {product.customizable || hasSizes ? t("orderNow") : t("add")}
          </button>
        </div>
      </div>
    </div>
  );
}
